
export interface Category {
  id: string;
  name: string;
  order: number;
  parentId?: string;
  isSecret?: boolean;
}

export enum ProductCategory {
  // Full Catalog Categories
  CUSTOM_BOX = 'Personalizados',
  VALENTINE = 'San Valentín',
  
  // Standard Categories
  BREAKFAST = 'Desayunos Artesanales',
  CAKES_AND_SWEETS = 'Tortas y Picadas dulces',
  CORPORATE = 'Empresas',
  BOARD = 'Tablas y Picadas saladas',
  KIDS = 'Infantiles y Bebés',
  FOOTBALL = 'Equipos de Fútbol'
}

export interface ProductOptionValue {
  id: string;
  name: string;
  price?: number;
  stock?: number;
  category?: string;
  image?: string;
  description?: string;
}

export interface ProductOption {
  id: string;
  name: string;
  type: 'select' | 'multi-select';
  values: ProductOptionValue[];
  maxSelections?: number;
  minSelections?: number;
  exactSelections?: number;
  allowMultipleUnits?: boolean;
  description?: string;
  isRequired?: boolean;
  optionType?: 'P' | 'C';
}

export interface GlobalOption extends ProductOption {
  optionType: 'P' | 'C'; // Personalization or Complement
}

export interface Presentation {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  description?: string;
  stock?: number | null;
}

export interface Product {
  id: string;
  name: string;
  subtitle?: string;
  description: string;
  price: number;
  oldPrice?: number;
  category: string;
  image: string;
  galleryImages?: string[];
  videoUrl?: string;
  tags?: string[];
  stock?: number;
  
  // Local (legacy) or specific options
  options?: ProductOption[];
  
  // Shared global options references
  globalOptionIds?: string[];
  
  // Presentations (Variants)
  hasPresentations?: boolean;
  presentations?: Presentation[];

  freeDelivery?: boolean;
  isHidden?: boolean;
  /** Fecha de vencimiento del producto (ISO string). Cuando faltan 30 días, el admin recibe una alerta. */
  expiryDate?: string | null;
}

export enum OrderStatus {
  NUEVO = 'Nuevo',
  PENDIENTE = 'Pendiente',
  PAGADO = 'Pagado',
  ENTREGADO = 'Entregado',
  CANCELADO = 'Cancelado'
}

export enum PaymentStatus {
  PENDING = 'Pendiente de pago',
  PAID = 'Pagado',
  REJECTED = 'Rechazado',
  REFUNDED = 'Reembolsado'
}

export enum PaymentMethod {
  TRANSFERENCIA = 'Transferencia Bancaria',
  TRANSFERENCIA_MP = 'Mercado Pago (Transferencia)',
  TARJETA_UALA = 'Ualá (Tarjeta)',
  EFECTIVO = 'Efectivo',
  PAGOS_INTERNACIONALES = 'Pagos Internacionales (PayPal o Western Union)',
  LINK_MP = 'Mercado Pago (Link de Pago)',
  TARJETA = 'Tarjeta de Crédito/Débito'
}

export interface TransferAccount {
  id: string;
  bankName: string;
  accountHolder: string;
  cbu: string;
  alias: string;
  isActive: boolean;
  type: 'CBU' | 'CVU';
}

export interface Coupon {
  id: string;
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  minPurchase?: number;
  isActive: boolean;
  expiryDate?: string;
}

export interface ShippingZone {
  id: string;
  name: string;
  price: number;
  neighborhoods?: string[];
  polygon?: { lat: number; lng: number }[];
  radiusKm?: number; // Hybrid support for radius-based zones
}

export interface ShippingSettings {
  baseCost: number;
  pricePerKm: number;
  maxKmForAutoPayment: number;
  isMercadoPagoEnabled?: boolean;
  specialCategoryId?: string;
  freeShippingThreshold?: number;
  storeCoordinates?: { lat: number; lng: number };
  zones?: ShippingZone[];
  surcharges?: {
    [key in PaymentMethod]?: number;
  };
}

export type SpecialLayout = 'default' | 'father' | 'mother' | 'christmas' | 'women' | 'easter' | 'children' | 'friend';

export interface UIContent {
  activeLayout?: SpecialLayout;
  [key: string]: any;
}

export interface Order {
  id: string;
  customerName: string;
  customerPhone: string;
  deliveryAddress: string;
  deliveryType?: 'DELIVERY' | 'PICKUP';
  deliveryDate: string;
  deliveryTime?: string;
  recipientName?: string;
  recipientPhone?: string;
  neighborhood?: string;
  reference?: string;
  isPrivateNeighborhood?: boolean;
  familyName?: string;
  blockLot?: string;
  houseNumber?: string;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  items: CartItem[];
  total: number;
  shippingCost?: number;
  shippingZone?: string;
  distanceKm?: number;
  status: OrderStatus;
  createdAt: string;
  notes?: string;
  externalPaymentId?: string;
  couponCode?: string;
  discountAmount?: number;
  transferAccountId?: string;
  paymentSurcharge?: number;
}

export interface CartItem {
  id: string;
  productId: string;
  quantity: number;
  selectedOptions?: {
    optionId: string;
    values: string[];
  }[];
  selectedPresentationId?: string;
}

export interface UserRole {
  email: string;
  role: 'admin' | 'editor';
}

export interface CustomerProfile {
  id: string;
  email: string;
  phone: string;
  name: string;
  totalOrders: number;
  totalSpent: number;
  firstOrderDate: string;
  lastOrderDate: string;
}

export interface StoreSettings {
  whatsappNumber: string;
  whatsappMessage: string;
  contactEmail: string;
  instagramUrl: string;
  facebookUrl: string;
  tiktokUrl: string;
  storeLocation: string;
  bannerText: string;
  bannerIsActive: boolean;
  shippingPolicy: string;
  returnPolicy: string;
}

export interface DayBusinessHours {
  isOpen: boolean;
  openTime: string;  // Format "HH:mm"
  closeTime: string; // Format "HH:mm"
}
export interface BusinessHours {
  [dayIndex: string]: DayBusinessHours; // "0" (Sunday) to "6" (Saturday)
}